import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useUI } from '../context/UIContext';
import { FiSave, FiUser, FiLock, FiSettings as FiSettingsIcon } from 'react-icons/fi';
import './SettingsPage.css';

const SettingsPage = () => {
    const { user, updateProfile, changePassword, isAdmin } = useAuth();
    const { isOffline } = useUI();
    const [activeTab, setActiveTab] = useState('profile');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    const [branch, setBranch] = useState(null);

    const [profileData, setProfileData] = useState({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        phone: user?.phone || '',
    });

    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });

    const [branchData, setBranchData] = useState({
        name: '',
        address: '',
        phone: '',
        email: '',
        taxRate: '',
        openingTime: '',
        closingTime: '',
    });

    useEffect(() => {
        if (user?.branchId) {
            fetchBranch();
        }
    }, [user?.branchId]);

    const fetchBranch = async () => {
        try {
            const response = await api.get(`/branches/${user.branchId}`);
            setBranch(response.data);
            setBranchData({
                name: response.data.name,
                address: response.data.address || '',
                phone: response.data.phone || '',
                email: response.data.email || '',
                taxRate: response.data.tax_rate,
                openingTime: response.data.opening_time,
                closingTime: response.data.closing_time,
            });
        } catch (error) {
            console.error('Failed to fetch branch:', error);
        }
    };

    const showMessage = (type, text) => {
        setMessage({ type, text });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    };

    const handleProfileSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await updateProfile(profileData);
            showMessage('success', 'Profile updated successfully!');
        } catch (error) {
            showMessage('error', error.response?.data?.message || 'Failed to update profile');
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            showMessage('error', 'Passwords do not match');
            return;
        }

        if (passwordData.newPassword.length < 6) {
            showMessage('error', 'Password must be at least 6 characters');
            return;
        }

        setLoading(true);
        try {
            await changePassword(passwordData.currentPassword, passwordData.newPassword);
            showMessage('success', 'Password changed successfully!');
            setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (error) {
            showMessage('error', error.response?.data?.message || 'Failed to change password');
        } finally {
            setLoading(false);
        }
    };

    const handleBranchSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.put(`/branches/${user.branchId}`, branchData);
            showMessage('success', 'Branch settings updated!');
            fetchBranch();
        } catch (error) {
            showMessage('error', error.response?.data?.message || 'Failed to update branch');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="settings-page">
            <div className="page-header">
                <div>
                    <h1>Settings</h1>
                    <p className="text-muted">Manage your account and branch settings</p>
                </div>
            </div>

            {message.text && (
                <div className={`alert alert-${message.type}`}>
                    {message.text}
                </div>
            )}

            <div className="settings-container">
                {/* Settings Tabs */}
                <div className="settings-tabs">
                    <button
                        className={`settings-tab ${activeTab === 'profile' ? 'active' : ''}`}
                        onClick={() => setActiveTab('profile')}
                    >
                        <FiUser /> Profile
                    </button>
                    <button
                        className={`settings-tab ${activeTab === 'password' ? 'active' : ''}`}
                        onClick={() => setActiveTab('password')}
                    >
                        <FiLock /> Password
                    </button>
                    {isAdmin && branch && (
                        <button
                            className={`settings-tab ${activeTab === 'branch' ? 'active' : ''}`}
                            onClick={() => setActiveTab('branch')}
                        >
                            <FiSettingsIcon /> Branch Settings
                        </button>
                    )}
                </div>

                {/* Settings Content */}
                <div className="settings-content">
                    {/* Profile Tab */}
                    {activeTab === 'profile' && (
                        <div className="card">
                            <div className="card-header">
                                <h3>Profile Information</h3>
                            </div>
                            <form onSubmit={handleProfileSubmit}>
                                <div className="card-body">
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label className="form-label">First Name</label>
                                            <input
                                                type="text"
                                                className="form-input"
                                                value={profileData.firstName}
                                                onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                                                required
                                                disabled={isOffline}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Last Name</label>
                                            <input
                                                type="text"
                                                className="form-input"
                                                value={profileData.lastName}
                                                onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                                                disabled={isOffline}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Email</label>
                                        <input
                                            type="email"
                                            className="form-input"
                                            value={user?.email || ''}
                                            disabled
                                        />
                                        <small className="text-muted">Email cannot be changed</small>
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Phone</label>
                                        <input
                                            type="tel"
                                            className="form-input"
                                            value={profileData.phone}
                                            onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                                            disabled={isOffline}
                                        />
                                    </div>
                                </div>
                                <div className="card-footer">
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={loading || isOffline}
                                        title={isOffline ? "Unavailable offline" : ""}
                                    >
                                        <FiSave /> Save Changes
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* Password Tab */}
                    {activeTab === 'password' && (
                        <div className="card">
                            <div className="card-header">
                                <h3>Change Password</h3>
                            </div>
                            <form onSubmit={handlePasswordSubmit}>
                                <div className="card-body">
                                    <div className="form-group">
                                        <label className="form-label">Current Password</label>
                                        <input
                                            type="password"
                                            className="form-input"
                                            value={passwordData.currentPassword}
                                            onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                                            required
                                            disabled={isOffline}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">New Password</label>
                                        <input
                                            type="password"
                                            className="form-input"
                                            value={passwordData.newPassword}
                                            onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                                            required
                                            minLength={6}
                                            disabled={isOffline}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Confirm New Password</label>
                                        <input
                                            type="password"
                                            className="form-input"
                                            value={passwordData.confirmPassword}
                                            onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                                            required
                                            disabled={isOffline}
                                        />
                                    </div>
                                </div>
                                <div className="card-footer">
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={loading || isOffline}
                                        title={isOffline ? "Unavailable offline" : ""}
                                    >
                                        <FiLock /> Update Password
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* Branch Settings Tab */}
                    {activeTab === 'branch' && isAdmin && branch && (
                        <div className="card">
                            <div className="card-header">
                                <h3>Branch Settings</h3>
                            </div>
                            <form onSubmit={handleBranchSubmit}>
                                <div className="card-body">
                                    <div className="form-group">
                                        <label className="form-label">Branch Name</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            value={branchData.name}
                                            onChange={(e) => setBranchData({ ...branchData, name: e.target.value })}
                                            required
                                            disabled={isOffline}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Address</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            value={branchData.address}
                                            onChange={(e) => setBranchData({ ...branchData, address: e.target.value })}
                                            disabled={isOffline}
                                        />
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label className="form-label">Phone</label>
                                            <input
                                                type="tel"
                                                className="form-input"
                                                value={branchData.phone}
                                                onChange={(e) => setBranchData({ ...branchData, phone: e.target.value })}
                                                disabled={isOffline}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Email</label>
                                            <input
                                                type="email"
                                                className="form-input"
                                                value={branchData.email}
                                                onChange={(e) => setBranchData({ ...branchData, email: e.target.value })}
                                                disabled={isOffline}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Sales Tax Rate (%)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="form-input"
                                            value={branchData.taxRate}
                                            onChange={(e) => setBranchData({ ...branchData, taxRate: e.target.value })}
                                            disabled={isOffline}
                                        />
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label className="form-label">Opening Time</label>
                                            <input
                                                type="time"
                                                className="form-input"
                                                value={branchData.openingTime}
                                                onChange={(e) => setBranchData({ ...branchData, openingTime: e.target.value })}
                                                disabled={isOffline}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Closing Time</label>
                                            <input
                                                type="time"
                                                className="form-input"
                                                value={branchData.closingTime}
                                                onChange={(e) => setBranchData({ ...branchData, closingTime: e.target.value })}
                                                disabled={isOffline}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="card-footer">
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={loading || isOffline}
                                        title={isOffline ? "Unavailable offline" : ""}
                                    >
                                        <FiSave /> Save Branch Settings
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SettingsPage;
